import React, { useContext, useState } from "react";
import { Box } from "Components/Base/Layout/Box";
import { LearningActivityCard } from "Components/Module/LearningActivityItem";
import ActivityDescription from "Components/Module/LearningActivityItem/Description";
import {
  useRating,
  getCardTitle,
  formatCourseDuration,
  renderTimeDisplay,
  getCardImage,
} from "Components/Module/LearningActivityItem/functions";
import config from "lib/config";
import Rating from "lib/components/Rating";
import { sanitize } from "lib/sanitizer.js";
import RestartCourse from "lib/components/MyLearning/components/restartCourse";
import { AddLinebreaksRemoveHtmlTags } from "lib/utils";
import { OnlineProgramChips } from "./CardChips";
import { OnlineProgramActions } from "./CardActions";
import { OBJECT_TYPE_ONLINE } from "../../../../constants";
import {
  useCourseCompletion,
  getDisplayDescription,
} from "../../../../functions";
import LangContext from "../../../../langContext";
import ProgramContext from "../../ProgramContext";
import CourseReason from "./CourseReason";

const OnlineProgramCard = (props) => {
  const languageContext = useContext(LangContext);
  const { course, displayImage, onRefresh, isPortalOverview, isPreview } =
    props;

  const [completed, setCompleted] = useCourseCompletion(course);
  const [restartCourse, setRestartCourse] = useState(false);
  const { rating, comment, updateRating } = useRating(course);
  const { description, shouldDisplay } = getDisplayDescription(
    course,
    isPortalOverview,
  );
  const ProgramSettings = useContext(ProgramContext);
  const displayTime = formatCourseDuration(course, languageContext);
  const displayRating =
    course.allowRating && !course.requested && !isPortalOverview;
  const { imageComponent } = getCardImage(
    course,
    displayImage,
    isPortalOverview,
  );

  const cardTitle = getCardTitle(course, isPortalOverview);

  const goToCourse = (courseId) => {
    document.location.href = `${config.urlRoot}education/entercourse.aspx?from=p&prid=${luvit.GetQueryString("activityid")}&courseid=${courseId}`;
  };

  const OnlineCardContent = () => {
    return (
      <>
        {isPortalOverview && (
          <Rating
            currentRating={course.avgRating}
            noOfRatings={course.noOfRatings}
            readOnly={true}
          />
        )}

        {displayRating && (
          <Rating
            currentRating={rating}
            currentComment={comment}
            type={OBJECT_TYPE_ONLINE}
            objectId={course.courseId}
            updateRating={updateRating}
            readOnly={false}
            managerView={ProgramSettings.managerView}
          />
        )}

        {shouldDisplay && (
          <ActivityDescription
            description={AddLinebreaksRemoveHtmlTags(sanitize(description))}
          />
        )}

        {isPortalOverview && (
          <CourseReason
            reason={course.reason}
            label={`${languageContext("reason")}: \n`}
          />
        )}

        <Box
          display="flex"
          justifyContent={displayTime ? "space-between" : "center"}
          width="100%"
        >
          {renderTimeDisplay(displayTime)}
        </Box>

        {restartCourse && (
          <RestartCourse
            initiallyOpen={false}
            title={course.title}
            dialogStyles={{ minWidth: "450px" }}
            course={course}
            closeRestartCourse={() => setRestartCourse(false)}
            refresh={() => (course.completed = false)}
            personalId={PERSONAL_ID}
            isManagerOrAdmin={false}
          />
        )}
      </>
    );
  };

  return (
    <LearningActivityCard
      id={`learningActivityCard${course.courseId}`}
      title={cardTitle}
      type={course.type}
      displayImage={displayImage}
      imageComponent={imageComponent}
      chips={
        <OnlineProgramChips
          course={course}
          completed={completed}
          languageContext={languageContext}
          isPortalOverview={isPortalOverview}
        />
      }
      content={<OnlineCardContent />}
      actions={
        <OnlineProgramActions
          course={course}
          languageContext={languageContext}
          isPortalOverview={isPortalOverview}
          ProgramSettings={ProgramSettings}
          onClick={() => goToCourse(course.courseId)}
        />
      }
    />
  );
};

export default OnlineProgramCard;
